/**
 * Per-account Rithmic metadata — FE-ONLY (localStorage, never the BE/DB). Captures the display-worthy
 * fields Rithmic sends in ResponseAccountList (template 303) — the friendly account name, the FCM/IB
 * routing ids, and the account currency — which the browser already receives on every connect but
 * otherwise DISCARDS (the order-data driver keeps only the id + creation time). Lets the connected
 * dialog show "Jane Doe / ZZ99007 / FCM · IB · USD" instead of just the raw id, and survives a
 * page reload (repopulated on the next connect). NEVER stores anything secret — no username, no
 * password (the username lives in the per-account login memory, the password only in the session
 * creds blob).
 *
 * Keyed by account id (the Rithmic account_id === AccountInfo.name). Rithmic-specific — CQG has no
 * analogue (it enriches the connected view from the BE accounts registry instead). See
 * `rithmicOrderData.ts` (writer, from onAccountList) and `RithmicConnectDialog.tsx` (reader).
 */
const STORAGE_KEY = 'rithmic.accountMeta.v1';

export interface RithmicAccountMeta {
  /** Friendly account name (ResponseAccountList.account_name), e.g. "Jane Doe". */
  accountName: string;
  /** Clearing-firm id (FCM), e.g. "Rithmic-FCM". */
  fcmId: string;
  /** Introducing-broker id (IB), e.g. "Prospects". */
  ibId: string;
  /** Account currency, e.g. "USD". */
  currency: string;
}

const EMPTY: RithmicAccountMeta = { accountName: '', fcmId: '', ibId: '', currency: '' };

type MetaMap = Record<string, RithmicAccountMeta>;

function readAll(): MetaMap {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    return parsed && typeof parsed === 'object' ? (parsed as MetaMap) : {};
  } catch { return {}; }
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

/** The captured metadata for one Rithmic account, or null if none has been seen this browser. */
export function readRithmicAccountMeta(accountId: string): RithmicAccountMeta | null {
  const e = readAll()[accountId];
  if (!e) return null;
  return { accountName: str(e.accountName), fcmId: str(e.fcmId), ibId: str(e.ibId), currency: str(e.currency) };
}

/**
 * Record the account-level fields from a ResponseAccountList frame. Fields are MERGED — a later frame
 * that omits a field keeps the previously-captured value (so a partial frame never blanks the name).
 * No-op without an account id, or when localStorage is unavailable (private mode / non-DOM test env).
 */
export function rememberRithmicAccountMeta(accountId: string, meta: Partial<RithmicAccountMeta>): void {
  if (!accountId) return;
  try {
    const map = readAll();
    const prev = map[accountId] ?? EMPTY;
    const next: RithmicAccountMeta = {
      accountName: str(meta.accountName) || prev.accountName,
      fcmId: str(meta.fcmId) || prev.fcmId,
      ibId: str(meta.ibId) || prev.ibId,
      currency: str(meta.currency) || prev.currency,
    };
    // Skip an entirely-empty write (e.g. a frame with only an id) so we never create a blank entry.
    if (!next.accountName && !next.fcmId && !next.ibId && !next.currency) return;
    map[accountId] = next;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch { /* storage unavailable (private mode / non-DOM env) — non-fatal */ }
}
